import React, { useEffect, useState } from 'react';
import Icon from '@mdi/react';
import { mdiCheckCircle, mdiAlertCircle, mdiInformation, mdiAlert, mdiClose } from '@mdi/js';
import './Toast.scss';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Show animation
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Auto dismiss
    const duration = toast.duration || 5000;
    const autoHideTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(autoHideTimer);
    };
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300); // Match CSS animation duration
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return mdiCheckCircle;
      case 'error':
        return mdiAlertCircle;
      case 'warning':
        return mdiAlert;
      case 'info':
      default:
        return mdiInformation;
    }
  };

  return (
    <div className={`toast toast-${toast.type} ${isVisible ? 'visible' : ''} ${isExiting ? 'exiting' : ''}`}>
      <div className="toast-content">
        <Icon path={getIcon()} size={1} className="toast-icon" />
        <span className="toast-message">{toast.message}</span>
        <button 
          className="toast-close" 
          onClick={handleDismiss}
          aria-label="Close notification"
        >
          <Icon path={mdiClose} size={0.8} />
        </button>
      </div>
    </div>
  );
};

export default Toast;